import {
  type Page,
  PageType,
  RevelioPageId,
} from '../components/PageConfigProvider/types';
import { FilterId, type PrimaryFilterId } from '../filters/types/filters';
import { getPathForDashboardId } from './page-config-lookup';

const SCREENER_PAGE_IDS: readonly RevelioPageId[] = [
  RevelioPageId.COMPANY_SCREENER,
  RevelioPageId.GEOGRAPHY_SCREENER,
  RevelioPageId.ROLE_SCREENER,
] as const;

const SCREENER_PAGE_ID_SET = new Set<string>(SCREENER_PAGE_IDS);

const SCREENER_PRIMARY_FILTER_MAP: Record<
  | RevelioPageId.COMPANY_SCREENER
  | RevelioPageId.GEOGRAPHY_SCREENER
  | RevelioPageId.ROLE_SCREENER,
  PrimaryFilterId
> = {
  [RevelioPageId.COMPANY_SCREENER]: FilterId.COMPANY,
  [RevelioPageId.GEOGRAPHY_SCREENER]: FilterId.GEOGRAPHY,
  [RevelioPageId.ROLE_SCREENER]: FilterId.ROLE,
};

export const isScreenerV2Page = (
  revelioPageId: RevelioPageId | string | undefined
): revelioPageId is
  | RevelioPageId.COMPANY_SCREENER
  | RevelioPageId.GEOGRAPHY_SCREENER
  | RevelioPageId.ROLE_SCREENER => {
  return revelioPageId !== undefined && SCREENER_PAGE_ID_SET.has(revelioPageId);
};

export const getPrimaryFilterIdForScreenerV2Page = (
  revelioPageId: RevelioPageId | string | undefined
): PrimaryFilterId | undefined => {
  if (!isScreenerV2Page(revelioPageId)) return undefined;
  return SCREENER_PRIMARY_FILTER_MAP[revelioPageId];
};

const PAGE_SCOPED_FILTER_GROUP_ID_SET = new Set<string>([
  ...SCREENER_PAGE_IDS,
  RevelioPageId.TALENT_DISCOVERY,
  RevelioPageId.POSTING_DISCOVERY,
]);

export const hasPageScopedFilterGroup = (
  revelioPageId: RevelioPageId | string | undefined
): revelioPageId is RevelioPageId =>
  revelioPageId !== undefined &&
  PAGE_SCOPED_FILTER_GROUP_ID_SET.has(revelioPageId);

export type EntityType =
  | 'company'
  | 'industry'
  | 'geography'
  | 'role'
  | 'skill'
  | 'activity'
  | 'report';
export const supportedEntityTypes: EntityType[] = [
  'company',
  'industry',
  'geography',
  'role',
  'skill',
  'activity',
  'report',
];

export const isValidEntityType = (
  value: EntityType | undefined
): value is EntityType => {
  return supportedEntityTypes.includes(value as EntityType);
};

export const entityTypeToFilterId = (
  entityType?: EntityType
): PrimaryFilterId | undefined => {
  switch (entityType) {
    case 'company':
      return FilterId.COMPANY;
    case 'industry':
      return FilterId.INDUSTRY;
    case 'geography':
      return FilterId.GEOGRAPHY;
    case 'role':
      return FilterId.ROLE;
    case 'skill':
      return FilterId.CUSTOM_SKILL;
    case 'activity':
      return FilterId.ACTIVITY;
    default:
      return undefined;
  }
};
export type RouteAction = 'edit' | 'clone';

interface BuildPathOptions {
  templateId?: string;
  action?: RouteAction;
  search?: string;
}

export const buildPathFromEntityType = (
  entityType: EntityType,
  options: BuildPathOptions = {}
) => {
  return buildPathFromEntity(entityType, options);
};

export const buildPrivateEntitySwitchPath = (
  targetPrimaryFilter: PrimaryFilterId,
  search?: string
) => {
  const targetEntityType = filterIdToEntityType(targetPrimaryFilter);
  return buildPathFromEntityType(targetEntityType, {
    search,
  });
};

export const buildPathFromRevelioPageId = (
  revelioPageId: string,
  options: Omit<BuildPathOptions, 'templateId'> = {}
) => {
  if (!getPathForDashboardId(revelioPageId)) {
    return '';
  }

  return getPathForDashboardId(revelioPageId) + (options.search ?? '');
};

export const buildPathFromPage = (
  page: Page,
  options: Omit<BuildPathOptions, 'templateId'> = {}
) => {
  if (page.type === PageType.REPORT) {
    return buildPathFromEntityType('report', {
      ...options,
      templateId: page.id,
    });
  }
  const primaryFilterId = page.tabs?.[0]?.tabContent?.primaryFilter?.id;
  if (primaryFilterId) {
    return buildPathFromEntityType(filterIdToEntityType(primaryFilterId), {
      ...options,
      templateId: page.id,
    });
  }

  const { action, search } = options;
  let url = `/custom-dash/${page.id}`;
  if (action) url += `/${action}`;
  if (search) url += search;
  return url;
};

const buildPathFromEntity = (
  entityType: EntityType,
  options: BuildPathOptions = {}
): string => {
  const { templateId, action, search } = options;
  let url = `/${entityType}`;
  if (templateId) url += `/${templateId}`;
  if (action) url += `/${action}`;
  if (search) url += search;
  return url;
};

export const filterIdToEntityType = (filterId: PrimaryFilterId): EntityType => {
  switch (filterId) {
    case FilterId.COMPANY:
      return 'company';
    case FilterId.INDUSTRY:
      return 'industry';
    case FilterId.GEOGRAPHY:
      return 'geography';
    case FilterId.ROLE:
      return 'role';
    case FilterId.CUSTOM_SKILL:
      return 'skill';
    case FilterId.ACTIVITY:
      return 'activity';
  }
};

export const extractEntityTypeFromPath = (pathname: string): EntityType => {
  return pathname.split('/')[1] as EntityType;
};
